import type { HomeCopy } from '../types/home';

export const homeEs: HomeCopy = {
  hero: {
    eyebrow: 'Para restaurantes, cafeterías y bares',
    title: {
      line1: 'Tu menú,',
      pre: 'en un ',
      highlight: 'código QR',
      post: ',',
      line3: 'en minutos.',
    },
    subtitle: {
      intro: 'Los clientes escanean una pequeña tarjeta en la mesa y tu menú se abre en su móvil. ',
      easyToUse: 'Fácil de usar',
      comma: ',',
      safeForGuests: 'seguro para los clientes',
      and: 'y',
      kindToNature: 'respetuoso con la naturaleza',
      outro: '. Sin aplicación, sin reimpresiones, sin complicaciones.',
    },
    primaryCta: 'Empieza gratis - no necesitas tarjeta',
    secondaryCta: 'Descubre cómo funciona',
    checklist: ['Listo en ~15 minutos', 'Funciona en cualquier móvil', 'Incluye plan gratuito'],
  },
  trustStrip: {
    ariaLabel: 'Por qué los restaurantes eligen SafestMenu',
    words: [
      'Fácil de usar',
      'Confianza de los clientes',
      'Seguro e higiénico',
      'Respetuoso con la naturaleza',
      'Sin aplicación necesaria',
      'Gratis para empezar',
    ],
  },
  why: {
    eyebrow: 'Por qué dejar el papel',
    heading: 'Los menús de papel cuestan dinero, propagan gérmenes y acaban en la basura.',
    cards: [
      {
        alt: 'Una mesa de restaurante impecable con un pequeño soporte de código QR en lugar de menús de papel',
        title: 'Un menú que nadie más ha tocado',
        text: {
          pre: 'Un menú de papel pasa por cientos de manos y casi nunca se lava. Con un código QR, cada cliente lo consulta en su propio móvil - el',
          highlight: 'menú más higiénico',
          post: 'que existe.',
        },
      },
      {
        alt: 'Un dueño de restaurante actualizando su menú desde el móvil en la cocina',
        title: 'Cambia un precio en 30 segundos',
        text: {
          pre: '¿Nuevo plato? ¿El proveedor subió los precios? Edítalo desde tu móvil y cada mesa ve la actualización al instante. Es realmente',
          highlight: 'fácil de usar',
          post: ' - si sabes escribir un mensaje de texto, ya sabes usar SafestMenu.',
        },
      },
      {
        alt: 'Una pila de menús de papel desgastados junto a un único soporte reutilizable de código QR y una planta verde',
        title: 'Imprime una vez, nunca más',
        text: {
          pre: 'Cada temporada de menús reimpresos y plastificados es dinero y papel en la basura. Una pequeña tarjeta por mesa es',
          highlight: 'respetuosa con la naturaleza',
          post: '- y con tu bolsillo.',
        },
      },
    ],
  },
  howItWorks: {
    eyebrow: 'Cómo funciona',
    heading: 'Tres pasos. Sin conocimientos técnicos.',
    steps: [
      {
        title: 'Escribe tu menú',
        text: 'Añade tus platos y precios. ¿No sabes cómo redactarlo? Nuestra IA escribe descripciones apetitosas e incluso crea fotos para los platos de los que no tienes ninguna.',
      },
      {
        title: 'Imprime tu código QR',
        text: 'Descarga tarjetas de mesa listas para imprimir en todos los tamaños habituales. Colócalas en las mesas, en el escaparate, en la barra... donde se sienten tus clientes.',
      },
      {
        title: 'Los clientes escanean y piden',
        text: 'Sin aplicación que instalar. El menú se abre en el navegador del móvil, con fotos, información de alérgenos y en su propio idioma.',
      },
    ],
  },
  scanImport: {
    eyebrow: 'Nuevo - importa en minutos',
    heading: '¿Ya tienes un menú? Empieza desde ahí.',
    paragraph:
      'No hace falta escribirlo todo de nuevo. Sube un PDF o unas fotos de tu menú impreso y SafestMenu lo lee - platos, precios, secciones - y te entrega un borrador limpio para revisar y publicar. Un menú típico cabe dentro de los créditos gratuitos que recibes al registrarte.',
    steps: [
      {
        title: 'Sube tu menú',
        text: 'Añade un PDF o hasta 40 fotos. Ves el coste exacto de antemano - 2 créditos por página - antes de que se cobre nada.',
      },
      {
        title: 'La IA lee cada página',
        text: 'Cada página se convierte en platos, precios y secciones editables, con el idioma y la moneda del menú detectados automáticamente.',
      },
      {
        title: 'Revisa y publica',
        text: 'Llegas a una pantalla de revisión ordenada, ajustas lo que haga falta y luego creas un menú nuevo o añades las páginas a uno que ya tengas.',
      },
    ],
  },
  productTour: {
    eyebrow: 'Míralo en acción',
    heading: 'Mira cómo cobra vida un menú.',
    paragraph:
      'De una página vacía a estar en la mesa. Este es el editor real: añade un plato, deja que la IA escriba la descripción, arrastra los elementos para ordenarlos y publica. No hace falta pedir una demo.',
  },
  aiStudio: {
    eyebrow: 'IA incorporada',
    heading: {
      pre: '¿Sin fotógrafo?',
      highlight: 'No hay problema.',
    },
    paragraph:
      'Haz una foto rápida - o ninguna - y deja que SafestMenu genere imágenes de platos bonitas y homogéneas. También escribe las descripciones apetitosas. Tú mantienes el control: eliges la foto que más te guste, la conservas y listo.',
    pills: [
      'Descripciones con IA · 1 crédito',
      'Fotos de platos con IA · 5 créditos',
      'Traducciones de menú · 1 crédito cada una',
      'Escaneo de menú · 2 créditos/página',
      'Sugerencias de alérgenos con IA · 1 crédito',
      'Los créditos no caducan',
    ],
  },
  features: {
    eyebrow: 'Todo incluido',
    heading: 'Diseñado para dueños de restaurantes, no para departamentos de IT.',
    sidebarCaption:
      'Cada vista de un plato se cuenta automáticamente - así ves qué desean tus clientes y destacas tus platos estrella.',
    items: [
      {
        title: 'Los precios se actualizan al instante',
        text: 'Edita cualquier cosa desde tu móvil. Cada código QR en cada mesa muestra siempre el menú más reciente.',
      },
      {
        title: 'Fotos y descripciones con IA',
        text: '¿Sin fotógrafo? La IA incorporada escribe descripciones que abren el apetito y genera fotos de tus platos por ti.',
      },
      {
        title: 'Los 14 alérgenos de la UE',
        text: 'Marca los alérgenos de cada plato una sola vez. Los clientes ven etiquetas claras en la mesa - se acabaron las suposiciones y las respuestas arriesgadas.',
      },
      {
        title: 'Seis idiomas incorporados',
        text: 'Los turistas también son clientes. Tu menú habla inglés, alemán, español, francés, italiano y rumano.',
      },
      {
        title: 'Traduce tu menú con un clic',
        text: 'La IA traduce cada plato manteniendo el tono de voz de tu menú, por un crédito cada uno. Si editas un plato más tarde, solo ese plato se vuelve a traducir - así se mantiene al día por céntimos.',
      },
      {
        title: 'Menús que cambian según el horario',
        text: 'Gestiona cartas independientes de desayuno, comida y cena. Configura el horario una sola vez - el mismo código QR siempre muestra el menú que estás sirviendo en ese momento.',
      },
      {
        title: 'Duplica cualquier menú',
        text: 'Copia un menú entero con sus platos y fotos, ajusta lo que haya cambiado y publica cuando esté listo. Ideal para cartas de temporada y segundos locales.',
      },
      {
        title: 'Descubre lo que desean tus clientes',
        text: 'Estadísticas sencillas muestran qué platos se consultan más - así sabes qué poner en la pizarra de sugerencias.',
      },
      {
        title: 'Tu estilo, tu marca',
        text: 'Elige tus colores y tipografías, añade tu logo y fotos de tus platos. Tu menú se parece a tu restaurante, no al nuestro.',
      },
      {
        title: 'Empieza desde tu menú de papel',
        text: '¿Ya tienes un menú impreso? Sube un PDF o unas fotos y la app lo convierte en platos y precios editables - un menú típico cabe dentro de tus créditos gratuitos de bienvenida.',
      },
      {
        title: 'La IA sugiere los alérgenos',
        text: '¿Editando un plato? Deja que la IA lea su nombre y descripción y proponga los 14 alérgenos de la UE por un crédito. Tú revisas y confirmas - un punto de partida, nunca la última palabra.',
      },
      {
        title: 'Los clientes filtran el menú ellos mismos',
        text: 'En la mesa, los clientes abren un filtro, eligen qué evitar o la dieta que necesitan, y los platos no aptos se apartan. Integrado, siempre activo, sin nada que configurar.',
      },
      {
        title: 'Convierte a tus clientes en reseñas de Google',
        text: 'Añade el enlace a tu reseña de Google y tu menú termina con un aviso amistoso - los clientes satisfechos tocan y dejan una reseña ahí mismo, en la mesa, mientras la comida sigue fresca.',
      },
    ],
  },
  guests: {
    eyebrow: 'A los clientes les encanta',
    heading: {
      pre: 'Un menú en el que tus clientes pueden',
      highlight: 'confiar de verdad',
      post: '.',
    },
    paragraph:
      'Fotos claras, precios honestos, alérgenos marcados en cada plato y todo el menú en su propio idioma. Los clientes piden con confianza - y los clientes seguros piden más.',
    checklist: [
      'Se abre en el navegador - nada que instalar',
      'Etiquetas de alérgenos en cada plato',
      'Un toque cambia todo el menú a su idioma',
    ],
  },
  pricing: {
    eyebrow: 'Precios',
    heading: 'Empieza gratis. Sigue gratis si te basta.',
    paragraph:
      'Tu primer menú con cinco platos es gratis - sin tarjeta de crédito. Cuando tu menú crezca, actualiza a platos ilimitados y más créditos de IA, para descripciones, fotos y traducciones de menú. Los seis idiomas están incluidos en todos los planes. Cancela cuando quieras.',
    price: '€0',
    priceCaption: 'para empezar',
    cta: 'Crea tu menú gratis',
    disclaimer: 'Sin tarjeta de crédito. Sin límite de tiempo.',
  },
  faq: {
    eyebrow: 'Preguntas frecuentes',
    heading: 'Lo que los propietarios de restaurantes preguntan sobre SafestMenu.',
    items: [
      {
        question: '¿Qué es SafestMenu y cuál es su sitio web oficial?',
        answer:
          'SafestMenu es una plataforma de menús con código QR para restaurantes, cafeterías y bares. Su sitio web oficial es safestmenu.com, y los propietarios gestionan sus menús en app.safestmenu.com.',
      },
      {
        question: '¿Cómo funciona un menú con código QR de SafestMenu?',
        answer:
          'Creas y publicas un menú digital y después imprimes su código QR para las mesas. Los clientes lo escanean con la cámara del móvil y el menú actualizado se abre en el navegador.',
      },
      {
        question: '¿Los clientes tienen que instalar una aplicación?',
        answer:
          'No. Los clientes abren el menú en el navegador de su móvil, sin descargar ninguna aplicación ni crear una cuenta.',
      },
      {
        question: '¿Puedo cambiar platos y precios después de publicar?',
        answer:
          'Sí. Edita el menú desde el móvil o el ordenador y publica la actualización. El mismo código QR impreso mostrará la versión más reciente.',
      },
      {
        question: '¿Puedo marcar los alérgenos y ofrecer el menú en varios idiomas?',
        answer:
          'Sí. Puedes etiquetar los platos con los 14 alérgenos de la UE, permitir que los clientes filtren platos y ofrecer el menú en inglés, alemán, español, francés, italiano y rumano.',
      },
      {
        question: '¿Puedo empezar gratis con un menú con código QR?',
        answer:
          'Sí. El plan gratuito incluye un menú con hasta cinco platos, no requiere tarjeta de crédito y no tiene límite de tiempo.',
      },
    ],
  },
  finalCta: {
    heading: {
      line1: 'Pruébalo ahora mismo -',
      highlight: 'escanea este código.',
    },
    paragraph:
      'Esto es exactamente lo que harán tus clientes en la mesa. Si te gusta lo que ves, tu propio menú está a unos quince minutos de distancia.',
    cta: 'Empieza tu menú hoy',
  },
  jsonLd: {
    appDescription:
      'Menús con código QR para restaurantes, cafeterías y bares. Crea un menú digital en minutos o importa tu menú existente desde un PDF o fotos, actualiza precios al instante, cambia de menú según el horario, marca platos como no disponibles, con etiquetas de alérgenos, un filtro de alérgenos para clientes y traducción del menú a seis idiomas.',
  },
};
